import React, { ReactNode } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';

export type AlertVariant = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
  variant: AlertVariant;
  title?: string;
  children: ReactNode;
  className?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const variantConfig: Record<
  AlertVariant,
  {
    icon: React.ComponentType<{ className?: string }>;
    containerClass: string;
    iconClass: string;
  }
> = {
  success: {
    icon: CheckCircleIcon,
    containerClass: 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800',
    iconClass: 'text-green-500 dark:text-green-400',
  },
  error: {
    icon: ExclamationCircleIcon,
    containerClass: 'bg-danger-50 border-danger-200 dark:bg-danger-900/20 dark:border-danger-800',
    iconClass: 'text-danger-500 dark:text-danger-400',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    containerClass: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800',
    iconClass: 'text-yellow-500 dark:text-yellow-400',
  },
  info: {
    icon: InformationCircleIcon,
    containerClass: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800',
    iconClass: 'text-blue-500 dark:text-blue-400',
  },
};

export const Alert: React.FC<AlertProps> = ({
  variant,
  title,
  children,
  className,
  action,
}) => {
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <div
      className={twMerge(
        clsx(
          'border rounded-lg p-4',
          config.containerClass,
          className
        )
      )}
    >
      <div className="flex gap-3">
        <Icon className={clsx('h-5 w-5 flex-shrink-0', config.iconClass)} />
        <div className="flex-1">
          {title && (
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              {title}
            </h3>
          )}
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {children}
          </div>
          {action && (
            <button
              onClick={action.onClick}
              className="mt-2 text-sm font-medium underline hover:no-underline"
            >
              {action.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
